import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  dayRange,
  formatKpi,
  formatKpiChange,
  hasKpis,
  budgetColumns,
  kpiColumns,
  kpiTable,
  previousRange,
  type KpiCell,
  type KpiColumn,
} from '../../audit/kpis';
import { getSubmittedAudits, type AuditRecord } from '../../data/audits';
import { equalsIgnoreCase } from '../../data/scope';
import { groupFamilies, type TemplateRow } from '../../data/templateAdmin';
import type { Store } from '../../data/types';
import { CsvButton } from '../../ui/CsvButton';
import { formatDay, isoDaysAgo, companyIsoDay } from '../../ui/dates';
import { DistrictLink, StoreLink } from '../../ui/entityLinks';
import { useSortableTable } from '../../ui/tableTools';
import { isPhoneWidth, PagerBar, PagerTop, useFitPageSize, usePager } from '../../ui/Pager';
import { kpiCsvCells, kpiCsvHeader } from './analyticsCsv';
import { getBudgetsForYears, getStoreBudgets, type StoreBudgetRow } from '../../data/storeBudgets';
import type { KpiSettingsMap } from '../../audit/budgets';
import { cellBudget, familyKpiSettings, visitExpected, yearsOf } from './kpiBudget';

/**
 * Analytics → Visit KPIs (owner rulings 2026-10-20): for a chosen time
 * range, the AVERAGE of every number/percent answer (src/audit/kpis.ts) and
 * its CHANGE against the same length of time just before — district
 * averages on top, one row per store below (sortable, so "lowest AutoPay %"
 * is one click). Each visit counts once. Changes are shown neutral (no
 * green/red): up is good for Occupancy, bad for AR 30+ %.
 */

type Preset = '30' | '90' | '180' | '365' | 'custom';
/** Earliest custom start: about two years ago. */
const MAX_CUSTOM_DAYS = 731;
const PRESET_LABEL: Record<Preset, string> = {
  '30': 'Last 30 days',
  '90': 'Last 90 days',
  '180': 'Last 6 months',
  '365': 'Last 12 months',
  custom: 'Custom range…',
};

/** The live (else newest non-draft) name of every family that has KPI questions — the DM Visit first. */
function kpiFamilies(rows: readonly TemplateRow[]) {
  return groupFamilies(rows)
    .filter((f) => f.rows.some((r) => r.status !== 'Draft' && hasKpis(r.definition)))
    .map((f) => ({ key: f.key, name: f.name, rows: f.rows }))
    .sort((a, b) => Number(b.key === VISIT_KEY) - Number(a.key === VISIT_KEY));
}

/** The DM Visit template's family key — its submissions are "visits"; any other template's are "audits" (DM run F10). */
const VISIT_KEY = 'dm_visit';
const countNoun = (familyKey: string | undefined, n: number) =>
  `${familyKey === VISIT_KEY ? 'visit' : 'audit'}${n === 1 ? '' : 's'}`;

export function VisitKpis({
  stores,
  districts,
  district,
  templateRows,
  reloadKey = 0,
}: {
  /** Changes when the page reloads its audits (e.g. after "Build missing summaries"). */
  reloadKey?: number;
  /** The viewer's stores (Test District already left out). */
  stores: readonly Store[];
  /** Districts the viewer can see (sorted). */
  districts: readonly string[];
  /** The page's district filter ('' = all). */
  district: string;
  templateRows: readonly TemplateRow[];
}) {
  const families = useMemo(() => kpiFamilies(templateRows), [templateRows]);
  // The DM Visit when there is one, else the first template with KPIs.
  const [family, setFamily] = useState('');
  const chosen = families.find((f) => f.key === family) ?? families.find((f) => f.key === VISIT_KEY) ?? families[0];
  const noun = (n: number) => countNoun(chosen?.key, n);
  const Noun = chosen?.key === VISIT_KEY ? 'Visits' : 'Audits';

  const [preset, setPreset] = useState<Preset>('90');
  const [from, setFrom] = useState(() => isoDaysAgo(89));
  const [to, setTo] = useState(() => companyIsoDay(new Date()));
  const range = useMemo(() => {
    if (preset === 'custom') {
      // At most ~2 years back (security review 2026-10-20): the comparison
      // doubles the read, and an ancient "from" would read every audit.
      // …and never past today: a far-future "to" would stretch the
      // comparison window back decades (verify round 2026-10-20).
      if (from < isoDaysAgo(MAX_CUSTOM_DAYS) || to > companyIsoDay(new Date())) return null;
      return dayRange(from, to);
    }
    return dayRange(isoDaysAgo(Number(preset) - 1), companyIsoDay(new Date()));
  }, [preset, from, to]);
  const previous = useMemo(() => (range ? previousRange(range) : null), [range]);

  // Visits from the start of the comparison range on (SubmittedAt indexed).
  // Tagged with the window they were read for: figures from a shorter,
  // older read are never shown under a longer range's label (QA review).
  const [loaded, setLoaded] = useState<{ since: string; key: number; list: AuditRecord[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const since = previous?.start ?? '';
  useEffect(() => {
    if (!since) return;
    let stale = false;
    setError(null);
    getSubmittedAudits({ since, kpis: true })
      .then((list) => {
        if (!stale) setLoaded({ since, key: reloadKey, list });
      })
      .catch((err: unknown) => {
        if (!stale) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      stale = true;
    };
  }, [since, reloadKey]);
  const audits = loaded && loaded.since === since && loaded.key === reloadKey ? loaded.list : null;

  // B8: budgets for the years the range touches — colours only; a failed read just leaves them off.
  const [budgetRows, setBudgetRows] = useState<{ key: string; rows: StoreBudgetRow[] } | null>(null);
  const budgetYearsKey = range ? yearsOf(range.start, range.end).join(',') : '';
  useEffect(() => {
    if (!budgetYearsKey) return;
    let stale = false;
    getBudgetsForYears(budgetYearsKey.split(',').map(Number))
      .then((rows) => {
        if (!stale) setBudgetRows({ key: budgetYearsKey, rows });
      })
      .catch(() => undefined);
    return () => {
      stale = true;
    };
  }, [budgetYearsKey]);
  const settings = useMemo(() => (chosen ? familyKpiSettings(chosen.rows) : null), [chosen]);
  const expectedOf = useMemo(
    () =>
      budgetRows && budgetRows.key === budgetYearsKey && chosen && budgetRows.rows.length > 0
        ? visitExpected(budgetRows.rows, chosen.key, settings, new Set(budgetColumns(chosen.rows).map((c) => c.id)))
        : undefined,
    [budgetRows, budgetYearsKey, chosen, settings],
  );

  const columns = useMemo<KpiColumn[]>(() => (chosen ? kpiColumns(chosen.rows) : []), [chosen]);
  const rowIds = useMemo(() => new Set((chosen?.rows ?? []).map((r) => r.id)), [chosen]);
  const scoped = useMemo(
    () => stores.filter((s) => !district || equalsIgnoreCase(s.district.trim(), district)),
    [stores, district],
  );
  const storeById = useMemo(() => new Map(scoped.map((s) => [Number(s.id), s])), [scoped]);
  // This template's staff visits at the viewer's stores (store self-audits stay out, as elsewhere in Analytics).
  const visits = useMemo(
    () =>
      (audits ?? [])
        .filter((a) => a.party !== 'store' && rowIds.has(a.templateId) && storeById.has(a.storeId))
        .map((a) => ({ storeId: a.storeId, submittedAt: a.submittedAt, startedAt: a.startedAt, kpis: a.kpis })),
    [audits, rowIds, storeById],
  );
  // Submitted in the range but saved before KPI tracking (no summary yet).
  const untracked = range
    ? visits.filter((v) => v.kpis === null && v.submittedAt >= range.start && v.submittedAt < range.end).length
    : 0;

  const tables = useMemo(() => {
    if (!range) return null;
    const base = { audits: visits, columns, range, previous, ...(expectedOf ? { expectedOf } : {}) };
    const districtOf = (id: number) => storeById.get(id)?.district.trim() ?? '';
    return {
      all: kpiTable({ ...base, groupOf: () => 'all' }).get('all') ?? null,
      byDistrict: kpiTable({ ...base, groupOf: (a) => districtOf(a.storeId).toLowerCase() || null }),
      byStore: kpiTable({ ...base, groupOf: (a) => a.storeId }),
    };
  }, [visits, columns, range, previous, storeById, expectedOf]);
  const anyBudget = useMemo(
    () => (tables?.byStore ? [...tables.byStore.values()].some((t) => [...t.cells.values()].some((c) => c.budget)) : false),
    [tables],
  );

  const storeRows = useMemo(
    () =>
      tables
        ? [...tables.byStore.entries()].flatMap(([id, t]) => {
            const store = storeById.get(id);
            return store ? [{ store, ...t }] : [];
          })
        : [],
    [tables, storeById],
  );
  type StoreRow = (typeof storeRows)[number];
  const getters: Record<string, (r: StoreRow) => string | number | null> = {
    store: (r) => r.store.storeNumber,
    visits: (r) => r.visits,
  };
  // "k:" prefix: a KPI question id like "store" can't replace a fixed column's sort.
  for (const c of columns) getters[`k:${c.id}`] = (r) => r.cells.get(c.id)?.avg ?? null;
  const { th, apply } = useSortableTable<StoreRow>(getters);
  // Phones (UI review 3, E4): one group column at a time — the districts side by side ran off the screen.
  const [phoneGroup, setPhoneGroup] = useState('');

  if (families.length === 0) {
    return <p className="muted">No audit template has number or percent questions yet, so there are no KPIs to show.</p>;
  }

  const multiDistrict = !district && districts.length > 1;
  const districtNames = new Map(districts.map((d) => [d.toLowerCase(), d]));
  // The overall table's columns: the total, then each district (multi-district viewers).
  const groups = [
    { key: '', label: multiDistrict ? 'All districts' : district || districts[0] || 'All stores', table: tables?.all ?? null },
    ...(multiDistrict
      ? [...(tables?.byDistrict.entries() ?? [])].sort((a, b) => a[0].localeCompare(b[0])).map(([key, t]) => ({ key, label: districtNames.get(key) ?? key, table: t }))
      : []),
  ];
  const phone = isPhoneWidth();
  const shownGroups = phone && groups.length > 1 ? groups.filter((g) => g.key === (groups.some((x) => x.key === phoneGroup) ? phoneGroup : '')) : groups;

  return (
    <section>
      <div className="list-tools" role="group" aria-label="Visit KPI filters">
        {families.length > 1 && (
          <select value={chosen?.key ?? ''} onChange={(e) => setFamily(e.target.value)} aria-label="Template">
            {families.map((f) => (
              <option key={f.key} value={f.key}>
                {f.name}
              </option>
            ))}
          </select>
        )}
        <select value={preset} onChange={(e) => setPreset(e.target.value as Preset)} aria-label="Time range">
          {(Object.keys(PRESET_LABEL) as Preset[]).map((p) => (
            <option key={p} value={p}>
              {PRESET_LABEL[p]}
            </option>
          ))}
        </select>
        {preset === 'custom' && (
          <span className="date-range">
            <input
              type="date"
              value={from}
              min={isoDaysAgo(MAX_CUSTOM_DAYS)}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
              aria-label="From"
            />
            to
            <input
              type="date"
              value={to}
              min={from}
              max={companyIsoDay(new Date())}
              onChange={(e) => setTo(e.target.value)}
              aria-label="To"
            />
          </span>
        )}
      </div>

      {!range ? (
        <p className="error">Pick a start date on or before the end date: at most two years back, ending today at the latest.</p>
      ) : error ? (
        <p className="error">{error}</p>
      ) : audits === null ? (
        <p className="muted">Loading {noun(2)}…</p>
      ) : (
        <>
          <p className="muted">
            {chosen?.name ?? ''}: {tables?.all?.visits ?? 0} submitted from{' '}
            {formatDay(range.start)} to {formatDay(new Date(new Date(range.end).getTime() - 1).toISOString())}. Each
            figure is the average of the {noun(2)} that recorded it; the small line is the change against the{' '}
            {Math.round((new Date(range.end).getTime() - new Date(range.start).getTime()) / 86_400_000)} days
            before{previous ? ` (${formatDay(previous.start)} – ${formatDay(new Date(new Date(previous.end).getTime() - 1).toISOString())})` : ''}.
            {untracked > 0 &&
              ` ${untracked} more ${untracked === 1 ? 'was' : 'were'} submitted before KPI tracking and ${untracked === 1 ? "isn’t" : "aren’t"} counted yet (an admin can build missing summaries).`}
          </p>

          <div className="viz-head">
            <h3>{multiDistrict ? 'By district' : 'Overall'}</h3>
            {tables?.all && (
              <CsvButton
                name={`kpis-${multiDistrict ? 'by-district' : 'overall'}`}
                header={kpiCsvHeader([multiDistrict ? 'District' : 'Group'], Noun, columns)}
                rows={() => [
                  ...(tables.all
                    ? [[multiDistrict ? 'All districts' : district || districts[0] || 'All stores', ...kpiCsvCells(tables.all, columns)]]
                    : []),
                  ...(multiDistrict
                    ? [...tables.byDistrict.entries()]
                        .sort((a, b) => a[0].localeCompare(b[0]))
                        .map(([key, t]) => [districtNames.get(key) ?? key, ...kpiCsvCells(t, columns)])
                    : []),
                ]}
              />
            )}
          </div>
          {/* KPIs as rows, the total and each district as columns — a dozen KPIs fit a 1080p page this way
              (UI review 2, V5: as columns they ran past the page edge), like the store page's KPI table. */}
          {phone && groups.length > 1 && (
            <select className="kpi-phone-group" value={shownGroups[0]?.key ?? ''} onChange={(e) => setPhoneGroup(e.target.value)} aria-label="District shown">
              {groups.map((g) => (
                <option key={g.key} value={g.key}>
                  {g.label}
                </option>
              ))}
            </select>
          )}
          <div className="table-wrap">
            <table className="kpi-table kpi-table-groups">
              <thead>
                <tr>
                  <th className="kpi-first">KPI</th>
                  {shownGroups.map((g) => (
                    <th key={g.key} className="kpi-group-head">
                      {g.key === '' ? <strong>{g.label}</strong> : <DistrictLink district={g.label} />}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="kpi-first">{Noun}</td>
                  {shownGroups.map((g) => (
                    <td key={g.key} className="kpi-cell">
                      {g.table?.visits ?? 0}
                    </td>
                  ))}
                </tr>
                {columns.map((c) => (
                  <tr key={c.id}>
                    <td className="kpi-first" title={c.section}>
                      {c.label}
                    </td>
                    {shownGroups.map((g) => (
                      <KpiTd key={g.key} cell={g.table?.cells.get(c.id)} column={c} noun={noun} settings={settings} />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="viz-head">
            <h3>By store</h3>
            {storeRows.length > 0 && (
              <CsvButton
                name="kpis-by-store"
                header={kpiCsvHeader(['Store number', 'Store', 'District'], Noun, columns)}
                rows={() =>
                  apply(storeRows).map((r) => [r.store.storeNumber, r.store.name, r.store.district, ...kpiCsvCells(r, columns)])
                }
              />
            )}
          </div>
          {storeRows.length === 0 ? (
            <p className="muted">No {noun(2)} in this range or the one before it.</p>
          ) : (
            <KpiStoreTable
              rows={apply(storeRows)}
              head={
                <>
                  {th('Store', 'store', 'kpi-first')}
                  {th(Noun, 'visits')}
                  {columns.map((c) => th(c.label, `k:${c.id}`, 'kpi-head'))}
                </>
              }
              columns={columns}
              Noun={Noun}
              noun={noun}
              settings={settings}
              multiDistrict={multiDistrict}
              resetKey={`${chosen?.key ?? ''}|${preset}|${district}`}
            />
          )}
          {anyBudget && <BudgetLegend />}
        </>
      )}
    </section>
  );
}

/** Under a KPI table with coloured cells (B8). */
export function BudgetLegend() {
  return (
    <p className="muted kpi-legend">
      Against each store’s monthly budget (counts) or goal (levels):
      <span className="kpi-dot kpi-green" aria-hidden /> meets it
      <span className="kpi-dot kpi-amber" aria-hidden /> within 10% (5 points for percentages)
      <span className="kpi-dot kpi-red" aria-hidden /> further off. Counts are month to date, so their budget is
      prorated to the visit’s day.
    </p>
  );
}

/**
 * Analytics → KPIs → By store (UI review 3): a page at a time sized to 80% of
 * the window with "Show all" (E2: 77 rows were 373–479% of it), and phone
 * cards — a store per card, its KPIs as label / value lines (E4: 13 KPI
 * columns ran sideways off a phone).
 */
function KpiStoreTable({
  rows,
  head,
  columns,
  Noun,
  noun,
  settings,
  multiDistrict,
  resetKey,
}: {
  rows: { store: Store; visits: number; cells: ReadonlyMap<string, KpiCell> }[];
  head: ReactNode;
  columns: readonly KpiColumn[];
  Noun: string;
  noun: (n: number) => string;
  settings: KpiSettingsMap | null;
  multiDistrict: boolean;
  resetKey: string;
}) {
  const bodyRef = useRef<HTMLTableSectionElement>(null);
  const size = useFitPageSize(bodyRef, { initial: 7, chromePx: 150, phone: 1 }); // a phone card holds every KPI (~13 lines): one a page
  const pager = usePager(rows, resetKey, size);
  return (
    <>
      <PagerTop pager={pager} />
      <div className="table-wrap">
        <table className="kpi-table kpi-table-compact board-cards">
          <thead>
            <tr>{head}</tr>
          </thead>
          <tbody ref={bodyRef}>
            {pager.items.map((r) => (
              <tr key={r.store.id}>
                <td className="kpi-first card-full card-title">
                  <StoreLink store={r.store} />
                  {/* Under the store rather than a column of its own — the KPI columns need the width (V5). */}
                  {multiDistrict && <div className="muted cell-sub">{r.store.district}</div>}
                </td>
                <td data-label={Noun}>{r.visits}</td>
                {columns.map((c) => (
                  <KpiTd key={c.id} cell={r.cells.get(c.id)} column={c} noun={noun} settings={settings} label={c.label} />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PagerBar pager={pager} noun="stores" allowAll />
    </>
  );
}

/** One KPI figure: the range's average, the change underneath. */
export function KpiTd({
  cell,
  column,
  noun = (n) => countNoun(VISIT_KEY, n),
  settings = null,
  label,
}: {
  cell: KpiCell | undefined;
  column: KpiColumn;
  /** Phone cards (board-cards): the KPI's name beside its value. */
  label?: string;
  noun?: (n: number) => string;
  /** B8: the template's KPI settings — the cell is coloured against its budget when it has one. */
  settings?: KpiSettingsMap | null;
}) {
  const change = formatKpiChange(cell?.change ?? null, column.type);
  const budget = cellBudget(cell, column, settings);
  const counted = cell ? `${cell.count} ${noun(cell.count)} recorded it` : undefined;
  return (
    <td
      data-label={label}
      className={budget ? `kpi-cell kpi-cell-${budget.band}` : 'kpi-cell'}
      title={budget ? `${budget.title}${counted ? ` · ${counted}` : ''}` : counted}
    >
      {formatKpi(cell?.avg ?? null, column.type)}
      {budget && <span className="kpi-sr"> ({budget.title})</span>}
      {change && <span className="kpi-change">{change}</span>}
    </td>
  );
}

/** Families that have KPI questions (exported for the page's view switch). */
export function hasKpiTemplates(rows: readonly TemplateRow[]): boolean {
  return kpiFamilies(rows).length > 0;
}

/**
 * Store page → Visit KPIs (owner ruling 2026-10-20): the same averages and
 * changes for ONE store, one table per template with KPIs that has visits
 * here, a KPI per row.
 */
export function StoreVisitKpis({
  audits,
  templateRows,
  storeId,
}: {
  /** The store's submitted audits (any range). */
  audits: readonly AuditRecord[];
  templateRows: readonly TemplateRow[];
  /** The store (B8: its budgets colour the averages); default = the audits' store. */
  storeId?: number;
}) {
  const [preset, setPreset] = useState<Exclude<Preset, 'custom'>>('90');
  // B8: this store's budgets (every year) — colours only; a failed read leaves them off.
  const budgetStore = storeId ?? audits[0]?.storeId;
  const [budgetRows, setBudgetRows] = useState<{ storeId: number; rows: StoreBudgetRow[] } | null>(null);
  useEffect(() => {
    if (budgetStore === undefined) return;
    let stale = false;
    getStoreBudgets(budgetStore)
      .then((rows) => {
        if (!stale) setBudgetRows({ storeId: budgetStore, rows });
      })
      .catch(() => undefined);
    return () => {
      stale = true;
    };
  }, [budgetStore]);
  const storeBudgets = budgetRows && budgetRows.storeId === budgetStore ? budgetRows.rows : null;
  const [picked, setPicked] = useState('');
  const [allKpis, setAllKpis] = useState(false);
  const range = useMemo(() => dayRange(isoDaysAgo(Number(preset) - 1), companyIsoDay(new Date())), [preset]);
  const previous = useMemo(() => (range ? previousRange(range) : null), [range]);
  const sections = useMemo(() => {
    if (!range) return [];
    return kpiFamilies(templateRows).flatMap((f) => {
      const ids = new Set(f.rows.map((r) => r.id));
      const visits = audits
        .filter((a) => a.party !== 'store' && ids.has(a.templateId))
        .map((a) => ({ storeId: a.storeId, submittedAt: a.submittedAt, startedAt: a.startedAt, kpis: a.kpis }));
      if (visits.length === 0) return [];
      const columns = kpiColumns(f.rows);
      const settings = familyKpiSettings(f.rows);
      const expectedOf = storeBudgets && storeBudgets.length > 0 ? visitExpected(storeBudgets, f.key, settings, new Set(budgetColumns(f.rows).map((c) => c.id))) : undefined;
      const t = kpiTable({ audits: visits, columns, range, previous, groupOf: () => 'store', ...(expectedOf ? { expectedOf } : {}) }).get('store');
      // Nothing in this range or the one before: no table of dashes (QA review).
      if (!t) return [];
      return [{ key: f.key, name: f.name, columns, settings, visits: t?.visits ?? 0, cells: t?.cells ?? new Map<string, KpiCell>() }];
    });
  }, [audits, templateRows, range, previous, storeBudgets]);

  if (sections.length === 0) return null;
  // One template at a time — the DM Visit by default (DM run F10: the
  // facility audit's figures led the block, read as "visits").
  const shown = sections.find((s) => s.key === picked) ?? sections[0]!;
  return (
    <section>
      <div className="page-head" style={{ alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>KPIs</h2>
        {/* The standard filter row, so the selects look and size like every other one (UI review U68). */}
        <div className="list-tools">
        {sections.length > 1 && (
          <select value={shown.key} onChange={(e) => setPicked(e.target.value)} aria-label="Template">
            {sections.map((s) => (
              <option key={s.key} value={s.key}>
                {s.name}
              </option>
            ))}
          </select>
        )}
        <select value={preset} onChange={(e) => setPreset(e.target.value as Exclude<Preset, 'custom'>)} aria-label="Time range">
          {(['30', '90', '180', '365'] as const).map((p) => (
            <option key={p} value={p}>
              {PRESET_LABEL[p]}
            </option>
          ))}
        </select>
        </div>
      </div>
      {[shown].map((s) => {
        // UI review V67 (80% rule): the KPIs with a budget first; with none, the first 6 — "Show all" for the rest.
        const budgeted = s.columns.filter((c) => cellBudget(s.cells.get(c.id), c, s.settings));
        const rest = s.columns.filter((c) => !budgeted.includes(c));
        const ordered = [...budgeted, ...rest];
        const lead = budgeted.length > 0 ? budgeted.length : Math.min(6, ordered.length);
        const rows = allKpis ? ordered : ordered.slice(0, lead);
        return (
        <div key={s.key}>
          <p className="muted">
            {s.name}: {s.visits} {countNoun(s.key, s.visits)} in the {PRESET_LABEL[preset].toLowerCase()}; the
            change is against the {PRESET_LABEL[preset].toLowerCase().replace('last ', '')} before.
          </p>
          <div className="table-wrap">
            <table className="kpi-table kpi-table-store">
              <thead>
                <tr>
                  <th>KPI</th>
                  <th>Average</th>
                  <th>Change</th>
                  <th>{s.key === VISIT_KEY ? 'Visits' : 'Audits'}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const cell = s.cells.get(c.id);
                  const budget = cellBudget(cell, c, s.settings);
                  return (
                    <tr key={c.id}>
                      <td className="kpi-store-name">{c.label}</td>
                      <td className={budget ? `kpi-cell kpi-cell-${budget.band}` : 'kpi-cell'} title={budget?.title}>
                        {formatKpi(cell?.avg ?? null, c.type)}
                        {budget && <span className="kpi-sr"> ({budget.title})</span>}
                      </td>
                      <td className="kpi-cell">{formatKpiChange(cell?.change ?? null, c.type) || '—'}</td>
                      <td className="kpi-cell">{cell?.count ?? 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {ordered.length > lead && (
            <p className="pager-bar">
              <span className="muted">
                {allKpis ? `All ${ordered.length} KPIs` : `${lead} of ${ordered.length} KPIs${budgeted.length > 0 ? ' — the ones with a budget' : ''}`}
              </span>
              <button type="button" className="btn btn-small" onClick={() => setAllKpis((v) => !v)}>
                {allKpis ? 'Show fewer' : `Show all ${ordered.length}`}
              </button>
            </p>
          )}
          {budgeted.length > 0 && <BudgetLegend />}
        </div>
        );
      })}
    </section>
  );
}
